import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/lib/stores/auth';
import { isStaff } from '@/lib/staffAccess';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import AdminSidebar from '@/components/admin/AdminSidebar';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { Breadcrumb, BreadcrumbItem } from '@/components/ui/Breadcrumb';
import { buildBreadcrumbs, isIdSegment } from '@/lib/breadcrumb';

export default function AdminLayout() {
  const { user, isLoading } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation('admin');

  const getBreadcrumbLabel = (seg: string): string => {
    if (isIdSegment(seg)) return seg;
    const key = `breadcrumb.${seg}` as any;
    if (!i18n.exists(key)) return seg;
    const val = t(key);
    return val === key ? seg : val;
  };

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(
    () => buildBreadcrumbs(location.pathname, (seg) => getBreadcrumbLabel(seg)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname, t],
  );

  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      const locale = location.pathname.split('/')[1] || 'en';
      navigate(`/${locale}/login`, { replace: true });
    } else if (user.role !== 'admin' && !isStaff(user)) {
      const locale = location.pathname.split('/')[1] || 'en';
      navigate(`/${locale}/dashboard`, { replace: true });
    }
  }, [user, isLoading, location.pathname, navigate]);

  if (isLoading) return <LoadingSpinner />;

  if (!user || (user.role !== 'admin' && !isStaff(user))) {
    return null;
  }

  // Layout mirrors the consumer/provider dashboard exactly:
  // <flex gap-6> [sidebar w-56] [main flex-1 min-w-0 (breadcrumb + content)].
  return (
    <div className="flex gap-6">
      <AdminSidebar />

      <main className="flex-1 min-w-0">
        {/* Breadcrumb */}
        {breadcrumbItems.length > 0 && (
          <Breadcrumb items={breadcrumbItems} className="mb-4" />
        )}

        <Outlet />
      </main>
    </div>
  );
}
